// W47b (2026-10-21): the real-session model for the Gifts screen, read from
// GET /v1/contacts/:id/gift-eligibility as the W47 api-server answers it
// (READ services/api-server/sinks/gift-eligibility-sink.mjs getForContact,
// sinks/gift-catalog.mjs GIFT_CATALOG and routes/stubs-w3c.mjs
// createContactGiftEligibilityHandler):
//
//   200 {result: 'OK', contactId, eligibility: {contactId, name, eligible,
//        status, reason, reasons[], catalog[18], catalogSource, lastGift,
//        pendingGift, inputs, notAvailable[], policy, actions, checkedAt,
//        source}, eligible, reason}
//   404 {result: 'NOT_FOUND', errorCode: 'CONTACT_NOT_FOUND', detail}
//
// `eligible` is never true from this source (policy.eligibleNeverTrueFromThisSource,
// MEASURED): it is false when a HubSpot-held check fails and null (unknown)
// otherwise, with every missing input named in `notAvailable`. The catalog
// carries names only: price is null with priceSource
// 'pricing-engine-not-bound' and stock 'unknown'. Nothing here types a
// price (feedback_pricing_engine_authority); the screen shows
// "Price from the pricing engine" instead.

export const GIFT_PRICE_PLACEHOLDER = 'Price from the pricing engine';
export const GIFT_STOCK_NOT_READ = 'Stock not read';
export const GIFT_NO_HISTORY = 'No gift on record';
export const GIFT_ELIGIBILITY_UNKNOWN_PREFIX = 'Eligibility unknown: missing ';

export type GiftCategoryCode = 't_shirt' | 'ball_cap' | 'half_gallon' | 'full_gallon';
export type GiftTone = 'navy' | 'charcoal' | 'sand' | 'gold' | 'teal' | 'blue' | 'purple' | 'aqua';

export interface GiftEligibilityReason {
  code: string;
  label: string;
  /** true = passed, false = failed, null = not checkable on this deployment. */
  ok: boolean | null;
  detail: string | null;
}

export interface LiveGiftCatalogItem {
  id: string;
  category: GiftCategoryCode | string;
  kind: string;
  name: string;
  variant: string | null;
  sizes: string[] | null;
  /** Always null from this source; a typed number would be a defect. */
  price: number | null;
  priceSource: string | null;
  stock: string | null;
}

export interface GiftNotAvailable {
  input: string;
  reason: string | null;
}

export interface GiftEligibilityView {
  contactId: string | null;
  name: string | null;
  eligible: boolean | null;
  status: string | null;
  reason: string | null;
  reasons: GiftEligibilityReason[];
  catalog: LiveGiftCatalogItem[];
  catalogSource: string | null;
  lastGift: { at: string | null; source: string | null; notAvailable: string | null };
  pendingGift: { value: string | null; notAvailable: string | null };
  notAvailable: GiftNotAvailable[];
  subscription: { active: boolean | null; status: string | null; product: string | null; nextChargeAt: string | null };
  shippingAddress: { complete: boolean | null; line1: string | null; city: string | null; state: string | null; zip: string | null };
  addToNextShipment: { available: boolean; reason: string | null; note: string | null };
  source: string | null;
}

export const GIFT_CATEGORY_TABS: ReadonlyArray<{ key: GiftCategoryCode; label: string; title: string; description: string }> = [
  { key: 't_shirt', label: 'T-shirts', title: 'Choose a T-shirt design', description: 'Select a design, then choose the size.' },
  { key: 'ball_cap', label: 'Ball caps', title: 'Choose a ball cap design', description: 'Select the exact cap design and color.' },
  { key: 'half_gallon', label: '½ gallons', title: 'Choose a half-gallon product', description: 'The selected gift is a half-gallon variant.' },
  { key: 'full_gallon', label: 'Full gallons', title: 'Choose a full-gallon product', description: 'The selected gift is a full-gallon variant.' },
];

/** Readable names for the W47 `notAvailable[].input` keys. */
export const NOT_AVAILABLE_LABELS: Record<string, string> = {
  tenure: 'tenure',
  lastGift: 'last gift',
  pendingGift: 'pending gift',
  programLimits: 'program limits',
  colicEnrollment: 'colic enrollment',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function optString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
function optNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}
function optBool(value: unknown): boolean | null {
  if (value === true) return true;
  if (value === false) return false;
  return null;
}

function mapReason(raw: unknown): GiftEligibilityReason | null {
  if (!isRecord(raw)) return null;
  const label = optString(raw.label);
  if (!label) return null;
  return { code: optString(raw.code) ?? label, label, ok: optBool(raw.ok), detail: optString(raw.detail) };
}

function mapCatalogItem(raw: unknown, index: number): LiveGiftCatalogItem | null {
  if (!isRecord(raw)) return null;
  const name = optString(raw.name);
  if (!name) return null;
  return {
    id: optString(raw.id) ?? `gift-${index}`,
    category: optString(raw.category) ?? 'other',
    kind: optString(raw.kind) ?? '',
    name,
    variant: optString(raw.variant),
    sizes: Array.isArray(raw.sizes) ? raw.sizes.filter((s): s is string => typeof s === 'string') : null,
    price: optNumber(raw.price),
    priceSource: optString(raw.priceSource),
    stock: optString(raw.stock),
  };
}

/** Map the route body (or the bare sink object); null when it is not an eligibility read. */
export function mapGiftEligibilityBody(body: unknown): GiftEligibilityView | null {
  if (!isRecord(body)) return null;
  const e = isRecord(body.eligibility) ? body.eligibility : body;
  if (!Array.isArray(e.reasons) && !Array.isArray(e.catalog) && !('eligible' in e)) return null;
  const inputs = isRecord(e.inputs) ? e.inputs : {};
  const sub = isRecord(inputs.subscription) ? inputs.subscription : {};
  const addr = isRecord(inputs.shippingAddress) ? inputs.shippingAddress : {};
  const lastGift = isRecord(e.lastGift) ? e.lastGift : {};
  const pendingGift = isRecord(e.pendingGift) ? e.pendingGift : {};
  const actions = isRecord(e.actions) ? e.actions : {};
  const add = isRecord(actions.addToNextShipment) ? actions.addToNextShipment : {};
  const eligibleRaw = 'eligible' in e ? e.eligible : body.eligible;
  return {
    contactId: optString(e.contactId) ?? optString(body.contactId),
    name: optString(e.name),
    eligible: optBool(eligibleRaw),
    status: optString(e.status),
    reason: optString(e.reason) ?? optString(body.reason),
    reasons: (Array.isArray(e.reasons) ? e.reasons : []).map(mapReason).filter((r): r is GiftEligibilityReason => r !== null),
    catalog: (Array.isArray(e.catalog) ? e.catalog : []).map(mapCatalogItem).filter((c): c is LiveGiftCatalogItem => c !== null),
    catalogSource: optString(e.catalogSource),
    lastGift: { at: optString(lastGift.at), source: optString(lastGift.source), notAvailable: optString(lastGift.notAvailable) },
    pendingGift: { value: optString(pendingGift.value), notAvailable: optString(pendingGift.notAvailable) },
    notAvailable: (Array.isArray(e.notAvailable) ? e.notAvailable : [])
      .filter(isRecord)
      .map(n => ({ input: optString(n.input) ?? 'unknown', reason: optString(n.reason) }))
      .filter(n => n.input !== 'unknown'),
    subscription: { active: optBool(sub.active), status: optString(sub.status), product: optString(sub.product), nextChargeAt: optString(sub.nextChargeAt) },
    shippingAddress: { complete: optBool(addr.complete), line1: optString(addr.line1), city: optString(addr.city), state: optString(addr.state), zip: optString(addr.zip) },
    addToNextShipment: { available: optBool(add.available) === true, reason: optString(add.reason), note: optString(add.note) },
    source: optString(e.source),
  };
}

/** One honest sentence for the eligibility state. */
export function eligibilitySummary(view: GiftEligibilityView): string {
  if (view.eligible === true) return 'Eligible';
  if (view.eligible === false) {
    const failed = view.reasons.find(r => r.ok === false);
    return `Not eligible: ${view.reason ?? failed?.detail ?? failed?.label ?? 'a check failed'}`;
  }
  const missing = view.notAvailable.map(n => NOT_AVAILABLE_LABELS[n.input] ?? n.input);
  return missing.length > 0 ? `${GIFT_ELIGIBILITY_UNKNOWN_PREFIX}${missing.join(', ')}` : 'Eligibility unknown';
}

/** Short pill label for the catalog head. */
export function eligibilityPill(view: GiftEligibilityView): string {
  if (view.eligible === true) return 'Eligible';
  if (view.eligible === false) return 'Not eligible';
  return 'Eligibility unknown';
}

/** Presentation tone for a catalog tile (design colour or product mark). */
export function toneFor(item: LiveGiftCatalogItem): GiftTone {
  const v = (item.variant ?? '').toLowerCase();
  if (item.category === 't_shirt' || item.category === 'ball_cap') {
    if (v.includes('charcoal')) return 'charcoal';
    if (v.includes('sand')) return 'sand';
    return 'navy';
  }
  const n = item.name.toLowerCase();
  if (n.startsWith('gut')) return 'gold';
  if (n.startsWith('osteo')) return 'teal';
  if (n.startsWith('joint')) return 'blue';
  if (n.startsWith('amino')) return 'purple';
  if (n.startsWith('hydra')) return 'aqua';
  return 'sand';
}

/** Two-letter product mark derived from the catalog name ("Osteo-MAX" -> "OM"). */
export function monogramFor(name: string): string {
  const parts = name.split(/[\s-]+/).filter(Boolean);
  const out = parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
  return out || name.slice(0, 2).toUpperCase();
}

export function catalogByCategory(catalog: LiveGiftCatalogItem[], category: GiftCategoryCode): LiveGiftCatalogItem[] {
  return catalog.filter(item => item.category === category);
}
